import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { makeSeoDescription } from "@/lib/seo-description";
import { BUSINESS_COUNTRIES } from "@/data/business-articles";

const CANONICAL = "https://lifewestway.com/business";
const TITLE = "Business Setup Abroad 2026 | LifeWestWay";

export const Route = createFileRoute("/business")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content: makeSeoDescription(
          "Start and run a business abroad in 2026 — company registration, visas for founders, taxes and banking for the USA, UK, Australia, Canada, Italy and Portugal."
        ),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content: makeSeoDescription(
          "Six countries, six founder playbooks — how to register a company abroad, which visa fits entrepreneurs, and what tax and banking rules apply in 2026."
        ),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: BusinessHub,
});

function BusinessHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Business" }]} />

      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
        <span aria-hidden className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Founders &amp; Owners · 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Business Setup <span className="text-accent">Abroad</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base sm:text-lg text-foreground/75">
            Starting a company in a new country is rarely about the paperwork alone. It is about
            picking the right legal structure, understanding which visa actually permits you to
            trade, opening a business bank account as a newcomer, and knowing what the tax
            authority expects in your first filing year. This hub collects our practical business
            guides for the United States, United Kingdom, Australia, Canada, Italy and Portugal.
          </p>
          <p className="mt-4 max-w-3xl text-base text-foreground/70">
            Each country guide is written and reviewed manually by the LifeWestWay editorial team,
            with the official registry, immigration and tax sources cited inside the article. We
            focus on what a founder must decide first — entity type, residency and work rights,
            registration costs, accounting duties and the ongoing compliance calendar — rather than
            generic startup advice you can find anywhere.
          </p>
        </div>
      </section>

      <section aria-label="Business guides by country" className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {BUSINESS_COUNTRIES.map((c) => {
          const a = c.articles[0];
          const inner = (
            <>
              <span aria-hidden className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${c.accent} opacity-0 transition-opacity group-hover:opacity-100`} />
              <div className="relative flex items-center justify-between">
                <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                  <span aria-hidden className="text-base leading-none">{c.flag}</span>
                  {c.country}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">2026</span>
              </div>
              <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
                {a ? a.title : `Doing Business in ${c.country}`}
              </h2>
              <p className="relative mt-3 text-sm text-foreground/70">
                {a
                  ? a.description
                  : `Company registration, founder visa routes, business banking and tax duties for ${c.country}. This guide is being written by our editorial team and publishes here as soon as it is reviewed.`}
              </p>
              <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
                <span className="text-xs font-medium text-foreground/60">
                  <span className="text-accent">◆</span> Entity · Visa · Tax · Banking
                </span>
                <span className="text-sm font-semibold text-accent">
                  {a ? <>Read guide <span aria-hidden>→</span></> : "In editorial review"}
                </span>
              </div>
            </>
          );

          const cls =
            "group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

          return a ? (
            <Link
              key={c.country}
              to={c.to}
              params={{ slug: a.slug }}
              title={`Read the business guide: ${a.title}`}
              className={cls}
            >
              {inner}
            </Link>
          ) : (
            <div key={c.country} className={cls}>
              {inner}
            </div>
          );
        })}
      </section>

      <section className="mt-12 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-border/60 bg-card/40 p-6">
          <h2 className="font-display text-2xl font-bold text-accent">What every founder decides first</h2>
          <h3 className="mt-4 text-lg font-semibold text-foreground">Legal structure</h3>
          <p className="mt-2 text-sm text-foreground/70">
            Sole trader, LLC, Ltd, Pty Ltd, SRL or Lda — the label changes by country but the trade-off
            is the same: personal liability and simple accounting on one side, limited liability with
            heavier filing duties on the other. Choose before you register; converting later costs money.
          </p>
          <h3 className="mt-4 text-lg font-semibold text-foreground">Immigration status</h3>
          <p className="mt-2 text-sm text-foreground/70">
            Registering a company and being allowed to work in it are two different permissions. Several
            countries let non-residents own shares but not draw a salary or act as a working director
            without the right visa. Confirm your work rights before you sign a lease or hire staff.
          </p>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card/40 p-6">
          <h2 className="font-display text-2xl font-bold text-accent">Running costs people underestimate</h2>
          <h3 className="mt-4 text-lg font-semibold text-foreground">Accounting and filings</h3>
          <p className="mt-2 text-sm text-foreground/70">
            Annual accounts, VAT/GST returns, payroll registration and social contributions usually cost
            more per year than the incorporation itself. Budget for a local accountant from month one.
          </p>
          <h3 className="mt-4 text-lg font-semibold text-foreground">Business banking</h3>
          <p className="mt-2 text-sm text-foreground/70">
            Newcomer founders are frequently declined by traditional banks until they hold a local
            address and tax number. Regulated fintech business accounts often bridge the first months,
            but check whether your registry or tax office accepts them for capital deposits.
          </p>
        </div>
      </section>

      <ArticleDisclaimer />

      <ShareBar />
    </main>
  );
}
